import {
  Milestone,
  MilestoneType,
  Rule,
  Ruleset,
} from 'types/dt-graphql-apollo-v2';
import { getAllMilestoneCollections } from './project-ruleset';

const addEdgeMilestoneRule = (milestoneId: string, ruleId: string) => ` 
      Add_Rule_milestones(endNode: "${milestoneId}", startNode: "${ruleId}") {
        endNode
        startNode
      }
      Add_Milestone_rules(endNode: "${ruleId}", startNode: "${milestoneId}") {
        endNode
        startNode
      }
`;

const addEdgeMilestoneRuleset = (milestoneId: string, rulesetId: string) => ` 
      Add_Ruleset_milestones(endNode: "${milestoneId}", startNode: "${rulesetId}") {
        endNode
        startNode
      }
      Add_Milestone_rulesets(endNode: "${rulesetId}", startNode: "${milestoneId}") {
        endNode
        startNode
      }
`;

const addEdgeRulesetRule = (rulesetId: string, ruleId: string) => `
      Add_Rule_rulesets(endNode: "${rulesetId}", startNode: "${ruleId}") {
        endNode
        startNode
      }
      Add_Ruleset_rules(endNode: "${ruleId}", startNode: "${rulesetId}") {
        endNode
        startNode
      }
`;

const removeEdgeRulesetRule = (rulesetId: string, ruleId: string) => `
      Remove_Rule_rulesets(endNode: "${rulesetId}", startNode: "${ruleId}") {
        endNode
        startNode
      }
      Remove_Ruleset_rules(endNode: "${ruleId}", startNode: "${rulesetId}") {
        endNode
        startNode
      }
`;

const removeEdgeRulesetMilestone = (milestoneId: string, rulesetId: string) => `
      Remove_Milestone_rulesets(endNode: "${rulesetId}", startNode: "${milestoneId}") {
        endNode
        startNode
      }
      Remove_Ruleset_milestones(endNode: "${milestoneId}", startNode: "${rulesetId}") {
        endNode
        startNode
      }
`;

const removeEdgeRuleMilestone = (milestoneId: string, ruleId: string) => `
  mutation RemoveEdgeRuleMilestone {
      Remove_Milestone_rules(endNode: "${ruleId}", startNode: "${milestoneId}") {
        endNode
        startNode
      }
      Remove_Rule_milestones(endNode: "${milestoneId}", startNode: "${ruleId}") {
        endNode
        startNode
      }
    }
`;

export function createRuleQuery(
  rule: Rule,
  milestoneId?: string,
  rulesetId?: string
) {
  const addRuleToMilestoneMutation = milestoneId
    ? addEdgeMilestoneRule(milestoneId, rule.externalId)
    : '';
  const addRuleToRulesetMutation = rulesetId
    ? addEdgeRulesetRule(rulesetId, rule.externalId)
    : '';
  const schemaType = rule?.schemaType ? `"${rule?.schemaType}"` : null;

  const query = `
        mutation CreateRule ($tags: [String], $rules: String, $invertedRules: String, $simpleRules: String,
                              $simpleInvertedRules: String, $advanceRules: String, $filters: String) {
          Create_Rule(
            id: "${rule.externalId}",
            customerDefinedId: """${rule.customerDefinedId}""",
            description: """${rule.description}""",
            severity: ${rule.severity},
            errorMessage: """${rule.errorMessage}""",
            name: """${rule.name}""",
            projectId: "${rule.projectId}",
            schemaType: ${schemaType},
            primaryKeyName: "${rule.primaryKeyName}",
            tags: $tags,
            rules: $rules,
            invertedRules: $invertedRules,
            simpleRules: $simpleRules,
            simpleInvertedRules: $simpleInvertedRules,
            advanceRules: $advanceRules,
            filters: $filters
            schemaCollection: { _directRelation: "${rule.projectId}" }
          ) {
            externalId
          }
          Add_SchemaCollection_rules(
            endNode: "${rule.externalId}"
            startNode: "${rule.projectId}"
          ) {
            endNode
            startNode
          }
          ${addRuleToMilestoneMutation}
          ${addRuleToRulesetMutation}
        }
      `;
  return query;
}

export function deleteRuleQuery(rule: Rule) {
  return `
     mutation DeleteRule {
        Delete_Rule(id: "${rule.externalId}") {
          externalId
        }
      }`;
}

export function updateRuleQuery(oldRule: Rule, updatedRule: Rule) {
  const oldRuleset = oldRule?.rulesets?.items || [];
  const updatedRuleset = updatedRule?.rulesets?.items || [];
  const rulesetToBeDeleted = oldRuleset?.filter(
    (ruleset1) =>
      !updatedRuleset?.some(
        (ruleset2) => ruleset2 && ruleset2.externalId === ruleset1?.externalId
      )
  );
  const rulesetToBeAdded = updatedRuleset?.filter(
    (ruleset1) =>
      !oldRuleset?.some(
        (ruleset2) => ruleset2 && ruleset2.externalId === ruleset1?.externalId
      )
  );

  const schemaType = updatedRule?.schemaType
    ? `"${updatedRule?.schemaType}"`
    : null;
  const rulesetToBeAddedMutation = rulesetToBeAdded
    ? `  ${rulesetToBeAdded?.map(
        (ruleset) =>
          `${addEdgeRulesetRule(ruleset?.externalId!, updatedRule.externalId)}`
      )}
      `
    : '';
  const rulesetToBeDeletedMutation = rulesetToBeDeleted
    ? `  ${rulesetToBeDeleted?.map(
        (ruleset) => `
              ${removeEdgeRulesetRule(
                ruleset?.externalId!,
                updatedRule?.externalId
              )}
            `
      )}
      `
    : '';

  const query = `
        mutation UpdateRule ($tags: [String], $rules: String, $invertedRules: String, $simpleRules: String,
                              $simpleInvertedRules: String, $advanceRules: String, $filters: String) {
          Update_Rule(
            id: "${updatedRule.externalId}",
            customerDefinedId: """${updatedRule.customerDefinedId}""",
            description: """${updatedRule.description}""",
            severity: ${updatedRule.severity},
            errorMessage: """${updatedRule.errorMessage}""",
            name: """${updatedRule.name}""",
            projectId: "${updatedRule.projectId}",
            schemaType: ${schemaType},
            primaryKeyName: "${updatedRule.primaryKeyName}",
            tags: $tags,
            rules: $rules,
            invertedRules: $invertedRules,
            simpleRules: $simpleRules,
            simpleInvertedRules: $simpleInvertedRules,
            advanceRules: $advanceRules,
            filters: $filters
          ) {
            externalId
          }         
          ${rulesetToBeAddedMutation}
          ${rulesetToBeDeletedMutation}
        }
      `;
  return query;
}

/* Ruleset mutations */
export function createRulesetMutation(ruleset: Ruleset, milestoneId?: string) {
  const addRulesetToMilestoneMutation = milestoneId
    ? addEdgeMilestoneRuleset(milestoneId, ruleset.externalId)
    : '';

  const query = `
        mutation CreateRuleset ($labels: [String], $filters: String){
          Create_Ruleset(
            id: "${ruleset.externalId}",
            description: """${ruleset.description}""",
            name: """${ruleset.name}""",
            projectId: "${ruleset.projectId}",
            schemaType: "${ruleset.schemaType}",
            labels: $labels,
            filters: $filters
          ) {
            externalId
          }
          Add_SchemaCollection_rulesets(
            endNode: "${ruleset.externalId}"
            startNode: "${ruleset.projectId}"
          ) {
            endNode
            startNode
          }
          ${addRulesetToMilestoneMutation}
        }
      `;
  return query;
}

export function deleteRulesetMutation(ruleset: Ruleset) {
  return `
    mutation DeleteRuleset {
      Delete_Ruleset(id: "${ruleset.externalId}") {
        externalId
      }
    }`;
}

export function updateRulesetMutation(
  oldRuleset: Ruleset,
  updatedRuleset: Ruleset
) {
  const query = `
        mutation UpdateRuleset ($labels: [String], $filters: String) {
          Update_Ruleset(
            id: "${updatedRuleset.externalId}",
            description: """${updatedRuleset.description}""",
            name: """${updatedRuleset.name}""",
            projectId: "${updatedRuleset.projectId}",
            schemaType: "${updatedRuleset.schemaType}",
            labels: $labels,
            filters: $filters
          ) {
            externalId
          }
        }
      `;
  return query;
}

/* Milestone mutations */
const milestoneDateIsValid = (date: string | null) => date && true; // date.year && date.month && date.day;

export function createMilestoneMutation(milestone: Milestone) {
  const schedule = milestone?.schedule ? `"${milestone.schedule}"` : null;
  const startActiveTime = milestone?.startActiveTime
    ? `"${milestone.startActiveTime}"`
    : null;
  const endActiveTime = milestone?.endActiveTime
    ? `"${milestone.endActiveTime}"`
    : null;
  const startScheduleTime = milestone?.startScheduleTime
    ? `"${milestone.startScheduleTime}"`
    : null;
  const endScheduleTime = milestone?.endScheduleTime
    ? `"${milestone.endScheduleTime}"`
    : null;

  const query = `
        mutation CreateMilestone {
          Create_Milestone(
            id: "${milestone.externalId}",
            description: """${milestone.description}""",
            name: """${milestone.name}""",
            orderIndex: ${milestone.orderIndex},
            projectId: "${milestone.projectId}",
            schedule: ${schedule},
            milestoneType: "${milestone.milestoneType}",
            startActiveTime: ${startActiveTime},
            endActiveTime: ${endActiveTime},
            startScheduleTime: ${startScheduleTime},
            endScheduleTime: ${endScheduleTime},
            schemaCollection: { _directRelation: "${milestone.projectId}" },
          ) {
            externalId
          }
          Add_SchemaCollection_milestones(
            endNode: "${milestone.externalId}"
            startNode: "${milestone.projectId}"
          ) {
            endNode
            startNode
          }
        }
      `;
  return query;
}

export function deleteMilestoneMutation(
  milestone: Milestone,
  milestones: Milestone[],
  rulesets: Ruleset[],
  rules: Rule[]
) {
  const milestoneToBeDeleted = milestone;
  const rulesetsToBeDeleted = rulesets.filter(
    (ruleset) => ruleset?.milestones?.items.length === 1
  );
  // in case a ruleset has multiple milestones, just remove this milestone instead of deleting the entire ruleset
  const rulesetsToBeRemoved = rulesets.filter(
    (ruleset) => ruleset?.milestones && ruleset?.milestones?.items.length > 1
  );

  const rulesToBeDeleted = rules.filter(
    (rule) => rule?.milestones?.items.length === 1
  );
  // in case a rule has multiple milestones, just remove this milestone instead of deleting the entire rule
  const rulesToBeRemoved = rules.filter(
    (rule) => rule?.milestones && rule?.milestones?.items.length > 1
  );

  const hasMilestoneToBeDeletedStartActiveTime =
    milestoneToBeDeleted.milestoneType === MilestoneType.Default &&
    milestoneDateIsValid(milestoneToBeDeleted?.startActiveTime || null);

  const allDefaultMilestonesExceptCurrent = getAllMilestoneCollections(
    milestones
  )?.filter((m) => m.externalId !== milestoneToBeDeleted.externalId);

  const milestoneToBeUpdatedWithStartActiveTime =
    hasMilestoneToBeDeletedStartActiveTime &&
    !!allDefaultMilestonesExceptCurrent?.length &&
    allDefaultMilestonesExceptCurrent[0];

  const query = `
        mutation DeleteMilestone {
          ${rulesetsToBeDeleted?.map(
            (ruleset) => `
                Delete_Ruleset(id: "${ruleset?.externalId}") {
                  externalId
                }
              `
          )}
          ${rulesetsToBeRemoved?.map(
            (ruleset) =>
              `${removeEdgeRulesetMilestone(
                milestoneToBeDeleted.externalId,
                ruleset.externalId
              )}
              `
          )}
          ${rulesToBeDeleted?.map(
            (rule) => `
                Delete_Rule(id: "${rule?.externalId}") {
                  externalId
                }
              `
          )}
          ${rulesToBeRemoved?.map(
            (rule) =>
              `${removeEdgeRuleMilestone(
                milestoneToBeDeleted.externalId,
                rule.externalId
              )}`
          )}
          ${
            hasMilestoneToBeDeletedStartActiveTime &&
            milestoneToBeUpdatedWithStartActiveTime &&
            milestoneToBeUpdatedWithStartActiveTime?.externalId
              ? `
                  Update_Milestone(
                    id: "${milestoneToBeUpdatedWithStartActiveTime.externalId}",
                    name: "${milestoneToBeUpdatedWithStartActiveTime.name}"
                    startActiveTime: "${milestoneToBeDeleted.startActiveTime}") 
                    {
                      externalId
                      startActiveTime
                    }
                `
              : ''
          }
          Delete_Milestone(
            id: "${milestoneToBeDeleted.externalId}",
          ) {
            externalId
          }
        }
      `;

  return query;
}

export function updateMilestoneMutation(
  oldMilestone: Milestone,
  updatedMilestone: Milestone
) {
  const schedule = updatedMilestone?.schedule
    ? `"${updatedMilestone.schedule}"`
    : null;
  const startActiveTime = updatedMilestone?.startActiveTime
    ? `"${updatedMilestone.startActiveTime}"`
    : null;
  const endActiveTime = updatedMilestone?.endActiveTime
    ? `"${updatedMilestone.endActiveTime}"`
    : null;
  const startScheduleTime = updatedMilestone?.startScheduleTime
    ? `"${updatedMilestone.startScheduleTime}"`
    : null;
  const endScheduleTime = updatedMilestone?.endScheduleTime
    ? `"${updatedMilestone.endScheduleTime}"`
    : null;

  const query = `
        mutation UpdateMilestone {
          Update_Milestone(
            id: "${updatedMilestone.externalId}",
            description: """${updatedMilestone.description}""",
            name: """${updatedMilestone.name}""",
            projectId: "${updatedMilestone.projectId}",
            orderIndex: ${updatedMilestone.orderIndex},
            schedule: ${schedule},
            milestoneType: "${updatedMilestone.milestoneType}",
            startActiveTime: ${startActiveTime},
            endActiveTime: ${endActiveTime},
            startScheduleTime: ${startScheduleTime},
            endScheduleTime: ${endScheduleTime},

          ) {
            externalId
          }
        }
      `;
  return query;
}
